import {flow, getParent, types} from 'mobx-state-tree';
import PositionModel from './position-model';
import config from '../config';
import CustomerModel from './customer-model.js';
import GroupModel from './group-model.js';
import DeviceService from 'src/services/device-service.js';

const isJsonValue = (value) =>
  value !== null && typeof value === 'object';

const DeviceModel = types
    .model('Device', {
      id: types.identifierNumber,
      uniqueId: types.string,
      name: types.string,
      attributes: types.frozen({}),
      category: types.maybeNull(types.string),
      contact: types.maybeNull(types.string),
      disabled: types.maybeNull(types.boolean),
      shareable: types.maybeNull(types.boolean),
      mirrorable: types.maybeNull(types.boolean),
      geofenceIds: types.maybeNull(types.array(types.frozen([]))),
      groupId: types.maybeNull(types.number),
      groupName: types.maybeNull(types.reference(GroupModel, {
        get(_, parent) {
          return getParent(parent, 3).group.findById(parent.groupId)?.name ?? null;
        },
        set(value, _) {
          return value.id;
        },
      })),
      lastUpdate: types.maybeNull(types.string),
      model: types.maybeNull(types.string),
      phone: types.maybeNull(types.string),
      positionId: types.maybeNull(types.number),
      status: types.maybeNull(types.string),
      positionBuffer: types.array(PositionModel),
      currentPosition: types.maybe(types.maybeNull(PositionModel)),
      withTrace: types.optional(types.boolean, false),
      customerId: types.maybeNull(types.number),
      customerName: types.maybeNull(types.reference(CustomerModel, {
        get(_, parent) {
          return getParent(parent, 3).customerStore.findById(parent.customerId)?.name ?? null;
        },
        set(value, __) {
          return value.id;
        },
      })),
      ownerCustomerId: types.maybeNull(types.number),
      ownerCustomerName: types.maybeNull(types.reference(CustomerModel, {
        get(_, parent) {
          return getParent(parent, 3).customerStore.findById(parent.ownerCustomerId)?.name ?? null;
        },
        set(value, __) {
          return value.id;
        },
      })),
      createdByUserId: types.maybeNull(types.number),
      createdAt: types.maybeNull(types.string),
      updatedAt: types.maybeNull(types.string),
      customerCpfCnpj: types.maybeNull(types.reference(CustomerModel, {
        get(_, parent) {
          return getParent(parent, 3).customerStore.findById(parent.customerId)?.cpfCnpj ?? null;
        },
        set(value, __) {
          return value.id;
        },
      })),
      customTraceLimit: types.maybe(types.integer),
      motionStreak: types.optional(types.boolean, false),
      motionState: types.optional(types.boolean, false),
      motionTime: types.maybeNull(types.string),
      motionDistance: types.optional(types.number, 0),
    })
    .actions((self) => ({
      // Campo simples com valor igual nao notifica ninguem, mas `attributes` e um valor congelado:
      // o socket entrega o dispositivo inteiro recem-lido do JSON, entao o objeto chega novo a cada
      // mensagem e a comparacao por referencia acusa mudanca que nao existe. Gravar so o que mudou
      // de fato evita invalidar derivados -- a lista de cadastro entre eles -- varias vezes por
      // segundo, com a frota inteira.
      update: (data) => {
        const changed = {};

        Object.entries(data).forEach(([key, value]) => {
          const current = self[key];
          const same = current === value ||
            (isJsonValue(current) && isJsonValue(value) &&
              JSON.stringify(current) === JSON.stringify(value));

          if (!same) changed[key] = value;
        });

        if (Object.keys(changed).length) Object.assign(self, changed);
      },
      updateAttributes: (attributes) => {
          self.attributes = attributes;
      },
      newPosition: (position) => {
        if (!self.positionBuffer.find(({id}) => id === position.id)) {
          self.positionBuffer = [
            position,
            ...self.positionBuffer.slice(0, self.customTraceLimit ?? config.traceLimit - 1),
          ];
        }
      },
      setBuffer: (positions) => self.positionBuffer.push(...positions),
      setCurrentPosition: (position) => (self.currentPosition = position),
      setTrace: (value) => (self.withTrace = value),
      setCustomTraceLimit: (value) => (self.customTraceLimit = value),
      clearBuffer: (_) => (self.positionBuffer = []),
      // Metros e milissegundos, o mesmo formato que a posicao carrega; o que nao foi editado fica como esta.
      // `attributes` e um valor congelado: troca-se o objeto inteiro, senao a escrita falha (ou nao avisa o card).
      updateAccumulator({totalDistance, hours, ignoreOdometerEnabled}) {
        const position = self.currentPosition;
        if (position?.attributes) {
          const attributes = {...position.attributes};
          if (totalDistance !== undefined) {
            attributes.totalDistance = totalDistance;
            // Com o toggle ligado o card mostra o totalDistance; o servidor faz o mesmo na posicao que grava.
            if (attributes.displayOdometerPlatform || ignoreOdometerEnabled) {
              attributes.displayOdometer = totalDistance;
              attributes.displayOdometerPlatform = true;
            }
          }
          if (hours !== undefined) {
            attributes.hours = hours;
          }
          position.attributes = attributes;
        }
        // O servidor grava o toggle no cadastro, mas nao emite o dispositivo pelo socket.
        if (ignoreOdometerEnabled) {
          self.attributes = {...self.attributes, 'report.ignoreOdometer': true};
        }
      },
      findImages: flow(function* () {
        const deviceService = new DeviceService();
        try {
          return (yield deviceService.findImages(self.id)).deviceImages;
        } catch (error) {
          console.error('Error fetching selected device images');
          return [];
        }
      }),
    }))
    .views((self) => ({
      getCustomer() {
        const {customerStore} = getParent(self, 3);
        return customerStore.findById(self.customerId);
      },
      hasPosition() {
        return self.currentPosition !== null;
      },
      isTag() {
        return self.model === 'TAG';
      },
    }));

export default DeviceModel;
